import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Button, Modal, Select, Upload } from "antd";
import Utilities from "../dependencies/custom/react-utilities";
import Setup from "../../js/pages/setup/Setup";
import ApproveDeclineSetup from "../../js/pages/setup/ApproveDeclineSetup";
import BoardingAllocation from "../pages/admissions/Housing";
import Bustracking from "../../js/pages/transport/Bustracking";
import Main from "../../js/components/layout/Main";
import ProtectedRoute from "../components/comps/protected_route";
import Settings from "../dependencies/custom/settings";
import Config, {
  fuelApproDeclCols,
  allFuelRequestsCols,
  maintApproDeclCols,
  allMaintRequestsCols,
} from "../../js/pages/setup/Config";
import useTable from "../../js/hooks/table";
import useDelete from "../hooks/rest_delete";
import Applicants from "../pages/admissions/Applicants";
import { useNavigate, useLocation } from "react-router-dom";
import ValuesStore, { useStore } from "../store/values-store";
import MyJSX from "../pages/setup/MyJSX";
import useExportPDF from "../hooks/pdf";
import MyUpload from "../pages/general/upload";
import CustomFunctions from "../dependencies/custom/custom-functions";
import TripHistory from "../pages/transport/BustrackingHistory";
import SystemSettings from "../pages/admin/SystemSettings";

const useAdminRoutes = () => {
  const valuesStore = ValuesStore();
  const perms = valuesStore.getValue("permissions");
  const myRoles =
    useStore((state) => state.myRoles) ||
    JSON.parse(sessionStorage.getItem("myRoles") || "[]");
  const isSystemAdmin =
    Array.isArray(myRoles) && myRoles.includes("System Administrator");

  // const location = useLocation();
  const pdfExport = useExportPDF();
  const [editRecord, setEditRecord] = useState();
  const [deleteResponse, setDeleteResponse] = useState();
  const [apprvDeclRecord, setApprDeclRecord] = useState();
  const rejectionRemark = useRef();
  const [itemToView, setItemToview] = useState();
  const [itemToViewSetup, setItemToViewSetup] = useState();
  const [rerender, setRerender] = useState(false);
  const myJSX = MyJSX();
  // console.log(perms)
  const table = useTable();
  const del = useDelete();
  const navigate = useNavigate();

  // Staff management state
  const [addStaffModalVisible, setAddStaffModalVisible] = useState(false);
  const [nonStaffUsers, setNonStaffUsers] = useState([]);
  const [nonStaffLoading, setNonStaffLoading] = useState(false);
  const [addStaffLoading, setAddStaffLoading] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [staffRevoking, setStaffRevoking] = useState({});
  const [staffTableRerender, setStaffTableRerender] = useState(false);

  const fetchNonStaffUsers = useCallback(async () => {
    setNonStaffLoading(true);
    const res = await Utilities.fetchWithAuth(
      "GET",
      `${Settings.urls.auth.listUsers}?is_staff=false`,
      undefined,
      navigate,
    );
    if (res?.ok) {
      setNonStaffUsers(Array.isArray(res.data) ? res.data : []);
    } else {
      Utilities.showNotification(
        undefined,
        res?.msg || "Failed to fetch users",
      );
    }
    setNonStaffLoading(false);
  }, [navigate]);

  const openAddStaffModal = useCallback(async () => {
    setSelectedEmails([]);
    setAddStaffModalVisible(true);
    await fetchNonStaffUsers();
  }, [fetchNonStaffUsers]);

  const handleAddStaff = useCallback(async () => {
    if (!selectedEmails.length) {
      Utilities.showNotification(undefined, "Please select at least one user");
      return;
    }
    setAddStaffLoading(true);
    let anyFailed = false;
    for (const email of selectedEmails) {
      const res = await Utilities.fetchWithAuth(
        "POST",
        Settings.urls.auth.makeStaff,
        { email, isStaff: true },
        navigate,
      );
      if (!res?.ok) {
        anyFailed = true;
        Utilities.showNotification(
          undefined,
          res?.msg || `Failed to grant staff access to ${email}`,
        );
      }
    }
    setAddStaffLoading(false);
    if (!anyFailed) {
      Utilities.showNotification(
        "Success",
        "Staff privileges granted successfully",
        "text-success",
      );
    }
    setAddStaffModalVisible(false);
    setStaffTableRerender((v) => !v);
  }, [selectedEmails, navigate]);

  const handleRevokeStaff = useCallback(
    async (record) => {
      setStaffRevoking((prev) => ({ ...prev, [record.userID]: true }));
      const res = await Utilities.fetchWithAuth(
        "POST",
        Settings.urls.auth.makeStaff,
        { email: record.email, isStaff: false },
        navigate,
      );
      if (res?.ok) {
        Utilities.showNotification(
          "Success",
          res?.data?.message || "Staff access revoked",
          "text-success",
        );
        setStaffTableRerender((v) => !v);
      } else {
        Utilities.showNotification(
          undefined,
          res?.msg || "Failed to revoke staff access",
        );
      }
      setStaffRevoking((prev) => ({ ...prev, [record.userID]: false }));
    },
    [navigate],
  );

  // Bulk upload state
  const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [templateDownloading, setTemplateDownloading] = useState(false);
  const [uploadFileList, setUploadFileList] = useState([]);

  const handleDownloadTemplate = useCallback(async () => {
    setTemplateDownloading(true);
    const token = Utilities.getCookie("access");
    try {
      const response = await fetch(
        `${Settings.urls.auth.employeesTemplate}/excel`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (!response.ok) {
        Utilities.showNotification(undefined, "Failed to download template");
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `employees_template.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      Utilities.showNotification(undefined, "Failed to download template");
    }
    setTemplateDownloading(false);
  }, []);

  const handleBulkUpload = useCallback(async () => {
    if (!uploadFileList.length) {
      Utilities.showNotification(undefined, "Please select a file to upload");
      return;
    }
    const file = uploadFileList[0].originFileObj || uploadFileList[0];
    const formData = new FormData();
    formData.append("file", file);
    setBulkUploadLoading(true);
    const res = await Utilities.fetchWithAuth(
      "POST",
      Settings.urls.auth.employeesBulkUpload,
      formData,
      navigate,
    );
    setBulkUploadLoading(false);
    if (res?.ok) {
      const importData = res?.data?.data;
      const errors = importData?.errors || [];
      const successCount = importData?.success ?? 0;
      const failedCount = importData?.failed ?? 0;
      const skippedCount = importData?.skipped ?? 0;

      if (failedCount > 0) {
        Utilities.showNotification(
          "Import Completed",
          `${successCount} imported, ${failedCount} failed${skippedCount > 0 ? `, ${skippedCount} skipped` : ""}`,
          "text-warning",
        );
      } else {
        Utilities.showNotification(
          "Success",
          res?.data?.message || "Users imported successfully",
          "text-success",
        );
      }

      errors.forEach((err) => {
        const description =
          typeof err === "string"
            ? err
            : err.error || err.reason || err.message || JSON.stringify(err);
        const identifier =
          err.email || err.phone || err.staff_id || err.identifier || "";
        Utilities.showNotification(
          identifier ? `Failed: ${identifier}` : "Record Failed",
          description,
          "text-danger",
        );
      });

      setBulkUploadModalVisible(false);
      setUploadFileList([]);
      setStaffTableRerender((v) => !v);
    } else {
      Utilities.showNotification(
        undefined,
        res?.msg || "Failed to import users",
      );
    }
  }, [uploadFileList, navigate]);

  const conf = Config(
    table,
    del,
    setEditRecord,
    onSetupDelete,
    pdfExport,
    navigate,
    setRerender,
    setItemToViewSetup,
    valuesStore,
    perms,
    { onRevokeStaff: handleRevokeStaff, staffRevoking },
  );
  const fuelConf = fuelApproDeclCols(
    table,
    Settings.urls.transport.fuel_approve,
    navigate,
    setApprDeclRecord,
    setItemToview,
    rejectionRemark,
    valuesStore,
    perms,
  );
  const allFuelConf = allFuelRequestsCols(
    table,
    Settings.urls.transport.fuelrequests,
    navigate,
    setItemToview,
    valuesStore,
    perms,
  );
  const maintApprvDeclCols = maintApproDeclCols(
    table,
    Settings.urls.transport.repair_approval,
    navigate,
    setApprDeclRecord,
    setItemToview,
    rejectionRemark,
    valuesStore,
    perms,
  );
  const allMaintRequests = allMaintRequestsCols(
    table,
    Settings.urls.transport.repair_requests,
    navigate,
    setItemToview,
    valuesStore,
    perms,
  );

  // function onSetupEdit(record) {
  //   setEditRecord(record);
  // }

  function onSetupDelete(a, b, c) {
    setDeleteResponse(b);
  }

  const location = useLocation();
  useEffect(() => {
    // console.log(location,itemToView,itemToViewSetup)
    setEditRecord(undefined);
    setItemToview(undefined);
    setItemToViewSetup(undefined);
  }, [location.pathname]);

  // useEffect(() => {
  //   console.log(editRecord)
  // }, [editRecord]);

  return [
    {
      path: "/admin",
      element: <Main />,
      children: [
        {
          path: "trip_history",
          element: (
            <ProtectedRoute>
              <TripHistory />
            </ProtectedRoute>
          ),
        },
        {
          path: "bus_tracking",
          element: (
            <ProtectedRoute>
              <Bustracking />
            </ProtectedRoute>
          ),
        },
        {
          path: "draft",
          element: (
            <ProtectedRoute>
              <Applicants status={"DRAFT"} title={"Draft Applications"} />
            </ProtectedRoute>
          ),
        },
        {
          path: "pending",
          element: (
            <ProtectedRoute>
              <Applicants
                status={"SUBMITTED"}
                title={"Submitted & Pending Applications"}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "verified",
          element: (
            <ProtectedRoute>
              <Applicants status={"VERIFIED"} title={"Verified Applicants"} />
            </ProtectedRoute>
          ),
        },
        {
          path: "approved",
          element: (
            <ProtectedRoute>
              <Applicants
                status={"INTERVIEW"}
                title={"Approved & Awaiting Interview Scores"}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "rejected",
          element: (
            <ProtectedRoute>
              <Applicants status={"REJECTED"} title={"Rejected Applicants"} />
            </ProtectedRoute>
          ),
        },
        {
          path: "admitted",
          element: (
            <ProtectedRoute>
              <Applicants status={"ADMITTED"} title={"Admitted Applicants"} />
            </ProtectedRoute>
          ),
        },

        {
          path: "classes",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.bootstrap.klass}
                saveEndpoint={Settings.urls.bootstrap.klass}
                keyName={"id"}
                label={conf.klasses.label}
                tableName={conf.klasses.tableName}
                columns={conf.klasses.columns}
              />
            </ProtectedRoute>
          ),
        },

        {
          path: "streams",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.bootstrap.stream}
                saveEndpoint={Settings.urls.bootstrap.stream}
                keyName={"id"}
                label={conf.stream.label}
                tableName={conf.stream.tableName}
                columns={conf.stream.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "subjects",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.bootstrap.subject}
                saveEndpoint={Settings.urls.bootstrap.subject}
                keyName={"id"}
                label={conf.subjects.label}
                tableName={conf.subjects.tableName}
                columns={conf.subjects.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "admission_status",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.bootstrap.status}
                saveEndpoint={Settings.urls.bootstrap.status}
                keyName={"id"}
                label={conf.statuses.label}
                tableName={conf.statuses.tableName}
                columns={conf.statuses.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "status_transition",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.bootstrap.statusTransition}
                saveEndpoint={Settings.urls.bootstrap.statusTransition}
                keyName={"id"}
                label={conf.klass_status_transitions.label}
                tableName={conf.klass_status_transitions.tableName}
                columns={conf.klass_status_transitions.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "nationality",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.bootstrap.nations}
                saveEndpoint={Settings.urls.bootstrap.nations}
                keyName={"id"}
                label={conf.nationality.label}
                tableName={conf.nationality.tableName}
                columns={conf.nationality.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "relationship",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.bootstrap.relationship}
                saveEndpoint={Settings.urls.bootstrap.relationship}
                keyName={"id"}
                label={conf.relationship.label}
                tableName={conf.relationship.tableName}
                columns={conf.relationship.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "preschool_grades",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.academics.preschool_grades}
                saveEndpoint={Settings.urls.academics.preschool_grades}
                keyName={"id"}
                label={conf.PRESCHOOL_GRADES.label}
                tableName={conf.PRESCHOOL_GRADES.tableName}
                columns={conf.PRESCHOOL_GRADES.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "preschool_learning_areas",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.academics.preschool_learning_areas}
                saveEndpoint={Settings.urls.academics.preschool_learning_areas}
                keyName={"id"}
                label={conf.PRESCHOOL_LEARNING_AREAS.label}
                tableName={conf.PRESCHOOL_LEARNING_AREAS.tableName}
                columns={conf.PRESCHOOL_LEARNING_AREAS.columns}
              />
            </ProtectedRoute>
          ),
        },

        {
          path: "accommodation",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings?.urls?.enrollment?.accommodation}
                saveEndpoint={Settings?.urls?.enrollment?.accommodation}
                keyName={"id"}
                label={conf.ACCOMMODATION.label}
                tableName={conf.ACCOMMODATION.tableName}
                columns={conf.ACCOMMODATION.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "boarding_allocation",
          element: (
            <ProtectedRoute>
              <BoardingAllocation />
            </ProtectedRoute>
          ),
        },
        {
          path: "roles",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.rbac.roles}
                saveEndpoint={Settings.urls.rbac.roles}
                keyName={"id"}
                label={conf.roles.label}
                tableName={conf.roles.tableName}
                columns={conf.roles.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "users",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.auth.staffList}
                saveEndpoint={Settings.urls.auth.adminSignup}
                keyName={"userID"}
                editKey="userID"
                label={conf?.users?.label}
                tableName={conf?.users?.tableName}
                columns={conf?.users?.columns}
                rerender={staffTableRerender}
                leadingButtons={
                  isSystemAdmin && (
                    <Button
                      type="primary"
                      className="border-0"
                      onClick={openAddStaffModal}
                      icon={<i className="fas fa-user-plus" />}
                    >
                      Make Staff
                    </Button>
                  )
                }
                extraButtons={
                  isSystemAdmin && (
                    <>
                      <Button
                        loading={templateDownloading}
                        className="btn-info border-0"
                        onClick={handleDownloadTemplate}
                        icon={<i className="fas fa-download" />}
                      >
                        Template
                      </Button>
                      <Button
                        className="border-0"
                        style={{
                          background: "#722ed1",
                          borderColor: "#722ed1",
                          color: "#fff",
                        }}
                        onClick={() => {
                          setUploadFileList([]);
                          setBulkUploadModalVisible(true);
                        }}
                        icon={<i className="fas fa-file-upload" />}
                      >
                        Import
                      </Button>
                    </>
                  )
                }
                extraJSX={
                  <>
                    <Modal
                      title="Make Staff Member"
                      open={addStaffModalVisible}
                      onOk={handleAddStaff}
                      onCancel={() => setAddStaffModalVisible(false)}
                      okText="Grant Staff Access"
                      confirmLoading={addStaffLoading}
                      destroyOnClose
                    >
                      <p>Select users to grant staff privileges:</p>
                      <Select
                        mode="multiple"
                        style={{ width: "100%" }}
                        placeholder="Search and select users..."
                        loading={nonStaffLoading}
                        value={selectedEmails}
                        onChange={setSelectedEmails}
                        optionFilterProp="label"
                        showSearch
                        options={nonStaffUsers.map((u) => ({
                          label: u.fullName
                            ? `${u.fullName} (${u.email})`
                            : u.email,
                          value: u.email,
                        }))}
                      />
                    </Modal>
                    <Modal
                      title="Import Users"
                      open={bulkUploadModalVisible}
                      onOk={handleBulkUpload}
                      onCancel={() => {
                        setBulkUploadModalVisible(false);
                        setUploadFileList([]);
                      }}
                      okText="Import"
                      confirmLoading={bulkUploadLoading}
                      destroyOnClose
                    >
                      <p className="mb-3 text-muted">
                        Upload an Excel or CSV file to bulk import users. Use
                        the <strong>Template</strong> button to download the
                        correct format first.
                      </p>
                      <Upload.Dragger
                        accept=".xlsx,.xls,.csv"
                        maxCount={1}
                        fileList={uploadFileList}
                        beforeUpload={() => false}
                        onChange={({ fileList }) => setUploadFileList(fileList)}
                      >
                        <p className="ant-upload-drag-icon">
                          <i className="fas fa-file-upload fa-2x" />
                        </p>
                        <p className="ant-upload-text">
                          Click or drag file here
                        </p>
                        <p className="ant-upload-hint">
                          Supported formats: .xlsx, .xls, .csv
                        </p>
                      </Upload.Dragger>
                    </Modal>
                  </>
                }
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "permissions",
          element: (
            <ProtectedRoute>
              <Setup
                getEndpoint={Settings.urls.rbac.permissions}
                keyName={"id"}
                label={conf.permissions.label}
                tableName={conf.permissions.tableName}
                columns={conf.permissions.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "pages",
          element: (
            <ProtectedRoute>
              <Setup
                getEndpoint={Settings.urls.rbac.pages}
                keyName={"id"}
                label={conf.pages.label}
                tableName={conf.pages.tableName}
                columns={conf.pages.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "user_roles",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                split={["roleIds"]}
                getEndpoint={Settings.urls.rbac.userRoles}
                saveEndpoint={Settings.urls.rbac.userRoles}
                keyName={"id"}
                label={conf.user_roles.label}
                tableName={conf.user_roles.tableName}
                columns={conf.user_roles.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "role_permissions",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.rbac.rolePermissions}
                saveEndpoint={Settings.urls.rbac.rolePermissions}
                split={["permissionIds"]}
                keyName={"id"}
                label={conf.role_permissions.label}
                tableName={conf.role_permissions.tableName}
                columns={conf.role_permissions.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "attendance_remarks",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.academics.attendance_remark}
                saveEndpoint={Settings.urls.academics.attendance_remark}
                keyName={"id"}
                label={conf.ATTENDANCE_REMARKS.label}
                tableName={conf.ATTENDANCE_REMARKS.tableName}
                columns={conf.ATTENDANCE_REMARKS.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "role_pages",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                split={["pageIds"]}
                getEndpoint={Settings.urls.rbac.rolePages}
                saveEndpoint={Settings.urls.rbac.rolePages}
                keyName={"id"}
                label={conf.role_pages.label}
                tableName={conf.role_pages.tableName}
                columns={conf.role_pages.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "buses",
          element: (
            <ProtectedRoute>
              {" "}
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.transport.buses}
                saveEndpoint={Settings.urls.transport.buses}
                keyName={"id"}
                label={conf?.buses?.label}
                tableName={conf?.buses?.tableName}
                columns={conf?.buses?.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "drivers",
          element: (
            <ProtectedRoute>
              {" "}
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.transport.drivers}
                saveEndpoint={Settings.urls.transport.drivers}
                keyName={"driverID"}
                label={conf?.drivers?.label}
                tableName={conf?.drivers?.tableName}
                editKey="driverID"
                columns={conf?.drivers?.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "routes",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.transport.route}
                saveEndpoint={Settings.urls.transport.route}
                keyName={"id"}
                label={conf?.routes?.label}
                tableName={conf?.routes?.tableName}
                columns={conf?.routes?.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "bus_stops",
          element: (
            <ProtectedRoute>
              {" "}
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.transport.busstop}
                saveEndpoint={Settings.urls.transport.busstop}
                keyName={"id"}
                label={conf?.busstops?.label}
                tableName={conf?.busstops?.tableName}
                columns={conf?.busstops?.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "transport_tariffs",
          element: (
            <ProtectedRoute>
              {" "}
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.transport.tariff}
                saveEndpoint={Settings.urls.transport.tariff}
                keyName={"id"}
                label={conf?.tariffs?.label}
                tableName={conf?.tariffs?.tableName}
                columns={conf?.tariffs?.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "transport_types",
          element: (
            <ProtectedRoute>
              {" "}
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.transport.transporttype}
                saveEndpoint={Settings.urls.transport.transporttype}
                keyName={"id"}
                label={conf?.transport_types?.label}
                tableName={conf?.transport_types?.tableName}
                columns={conf?.transport_types?.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "generated_fuel_coupons",
          element: (
            <ProtectedRoute>
              {" "}
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.transport.fuel_coupons}
                // saveEndpoint={Settings.urls.transport.fuel_coupons}
                keyName={"id"}
                label={conf?.fuel_coupons?.label}
                tableName={conf?.fuel_coupons?.tableName}
                columns={conf?.fuel_coupons?.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "driver_bus_routes",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.transport.driverbusroute}
                saveEndpoint={Settings.urls.transport.driverbusroute}
                keyName={"id"}
                label={conf?.driverbusroute?.label}
                tableName={conf?.driverbusroute?.tableName}
                columns={conf?.driverbusroute?.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "my_fuel_requests",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.transport.myfuelrequest}
                saveEndpoint={Settings.urls.transport.fuelrequests}
                keyName={"id"}
                label={"My Fuel Requests"}
                tableName={conf?.fuelrequest?.tableName}
                setItemToview={setItemToViewSetup}
                itemToView={itemToViewSetup}
                viewJSX={myJSX.fuelJSX}
                columns={conf?.fuelrequest?.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "pending_L1_approval",
          element: (
            <ProtectedRoute>
              {" "}
              <ApproveDeclineSetup
                getEndpoint={Settings.urls.transport.pendingL1_approval}
                saveEndpoint={Settings.urls.transport.fuel_approve}
                keyName={"id"}
                label={"Fuel Requests Pending L1 Approval"}
                tableName={fuelConf?.fuelrequest?.tableName}
                record={apprvDeclRecord}
                setRecord={setApprDeclRecord}
                setItemToview={setItemToview}
                itemToView={itemToView}
                viewJSX={myJSX.fuelJSX}
                columns={fuelConf?.fuelrequest?.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "pending_L2_approval",
          element: (
            <ProtectedRoute>
              {" "}
              <ApproveDeclineSetup
                getEndpoint={Settings.urls.transport.pendingL2_approval}
                saveEndpoint={Settings.urls.transport.fuel_approve}
                keyName={"id"}
                label={"Fuel Requests Pending L2 Approval"}
                tableName={fuelConf?.fuelrequest?.tableName}
                record={apprvDeclRecord}
                setRecord={setApprDeclRecord}
                setItemToview={setItemToview}
                itemToView={itemToView}
                viewJSX={myJSX.fuelJSX}
                columns={fuelConf?.fuelrequest?.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "all_fuel_requests",
          element: (
            <ProtectedRoute>
              {" "}
              <ApproveDeclineSetup
                getEndpoint={Settings.urls.transport.fuel_requests}
                saveEndpoint={undefined}
                keyName={"id"}
                label={"All Fuel Requests"}
                tableName={allFuelConf?.fuelrequest?.tableName}
                itemToView={itemToView}
                setItemToview={setItemToview}
                viewJSX={myJSX.fuelJSX}
                headingKey={"requestNumber"}
                columns={allFuelConf?.fuelrequest?.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "my_maintenance_requests",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                getEndpoint={Settings.urls.transport.myrepairrequest}
                saveEndpoint={Settings.urls.transport.repair_requests}
                keyName={"id"}
                label={"My Maintenance Requests"}
                tableName={conf?.maintenance_request?.tableName}
                columns={conf?.maintenance_request?.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "pending_maintenance_requests",
          element: (
            <ProtectedRoute>
              {" "}
              <ApproveDeclineSetup
                getEndpoint={Settings.urls.transport.repair_requests}
                saveEndpoint={Settings.urls.transport.repair_approval}
                keyName={"id"}
                label={maintApprvDeclCols.request.label}
                tableName={maintApprvDeclCols.request.tableName}
                record={apprvDeclRecord}
                setRecord={setApprDeclRecord}

                setItemToview={setItemToview}
                itemToView={itemToView}
                viewJSX={myJSX.maintenanceJSX}

                columns={maintApprvDeclCols.request.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "all_maintenance_requests",
          element: (
            <ProtectedRoute>
              {" "}
              <ApproveDeclineSetup
                getEndpoint={Settings.urls.transport.repair_requests}
                saveEndpoint={undefined}
                keyName={"id"}
                label={"All Maintenance Requests"}
                tableName={allMaintRequests?.request?.tableName}
                itemToView={itemToView}
                setItemToview={setItemToview}
                viewJSX={myJSX.maintenanceJSX}
                headingKey={"maintenanceRequestNumber"}
                columns={allMaintRequests?.request?.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "upload",
          element: (
            <ProtectedRoute>
              <MyUpload
                title={"Upload Maintenance Jobcard Invoice"}
                description={"Maintenance Jobcard Invoice"}
                form={myJSX.form}
                onUpload={CustomFunctions.uploadJobCardInvoice}
                formItems={myJSX.invoiceUploadJSX}
                fileResponseKey={"invoiceData"}
                deleteEndpoint={
                  Settings?.urls?.transport?.repair_invoice_delete
                }
                normalizedKeys={{
                  name: "invoiceFileName",
                  url: "invoiceFile",
                  id: "id",
                }} //for normalizing file object keys
                fileFetchEndpoint={Settings?.urls?.transport?.repair_invoice}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "system_settings",
          element: (
            <ProtectedRoute>
              <SystemSettings />
            </ProtectedRoute>
          ),
        },
      ],
    },
  ];
};

export default useAdminRoutes;
